import { useEffect, useRef, useState } from 'react';
import '../styles/ar.css';
import MainButton from '../components/MainButton';
import MissionHeader from '../components/MissionHeader';
import Debrief from '../components/Debrief';

type MindArImageSystem = {
    start: () => Promise<void>;
    stop: () => void;
};

type ASceneEl = HTMLElement & {
    // A-Frame ajoute ça sur <a-scene>
    hasLoaded?: boolean;

    systems?: Record<string, unknown> & {
        'mindar-image-system'?: MindArImageSystem;
    };
};

type Step = 'intro' | 'ar' | 'result';

const AR = () => {
    // État pour stocker la largeur et la hauteur actuelles de .siteWrapper
    const [dimensions, setDimensions] = useState({ width: 640, height: 1136 });

    const sceneRef = useRef<ASceneEl | null>(null);
    const target0 = useRef<HTMLElement | null>(null);
    const target1 = useRef<HTMLElement | null>(null);
    const target2 = useRef<HTMLElement | null>(null);

    const [isCorrectAnswer, setIsCorrectAnswer] = useState<boolean | null>(null);
    const [step, setStep] = useState<Step>('intro');
    const [currentTargetIndex, setCurrentTargetIndex] = useState<number | null>(null);

    // Stockage des données des cartes AR avec les vidéos associées
    // const ARCards = [{ video: './ar/video-0.mp4' }, { video: './ar/video-1.mp4' }];

    const targetRatio = 1335 / 709; // height/width

    // Fonction pour arrêter toutes les vidéos et optionnellement les remettre à 0
    const stopAllVideos = (reset = true) => {
        const videos = document.querySelectorAll("video[id^='video-']");
        videos.forEach((v) => {
            const video = v as HTMLVideoElement;
            video.pause();
            if (reset) video.currentTime = 0;
        });
    };

    // Fonction pour cacher toutes les targets
    const hideAllTargets = () => {
        const targets = document.querySelectorAll('[mindar-image-target]');
        targets.forEach((el) => {
            (el as HTMLElement).setAttribute('visible', 'false');
        });
    };

    const resumeCurrentVideo = () => {
        if (currentTargetIndex == null) return;

        // si la target est encore visible/détectée, rejoue la vidéo
        const targetEl = document.querySelector(
            `[mindar-image-target][data-target-index="${currentTargetIndex}"]`,
        ) as HTMLElement | null;

        const isVisible = targetEl?.getAttribute('visible') === 'true';

        if (!isVisible) return;

        const video = document.querySelector(
            `#video-${currentTargetIndex}`,
        ) as HTMLVideoElement | null;
        if (video) {
            // au choix: reprendre là où ça s'est arrêté
            video.play().catch(() => {});
        }
    };

    // Fonction pour montrer toutes les targets (utile pour le debug)
    const showAllTargets = () => {
        const targets = document.querySelectorAll('[mindar-image-target]');
        targets.forEach((el) => (el as HTMLElement).setAttribute('visible', 'true'));
    };

    // Gestion de la mise à l'échelle en fonction de la taille de la fenêtre
    useEffect(() => {
        const updateDimensions = () => {
            const viewportWidth = document.documentElement.clientWidth; // Exclut la barre de défilement
            const windowHeight = window.innerHeight;

            const widthScaleFactor = viewportWidth / 640;
            const heightScaleFactor = windowHeight / 1136;

            const scaleFactor = Math.min(widthScaleFactor, heightScaleFactor);

            // Mettez à jour la taille de police basée sur le scaleFactor
            document.documentElement.style.setProperty(
                '--scale-font-size',
                `${33 * scaleFactor}px`,
            );

            setDimensions({
                width: 640 * scaleFactor,
                height: 1136 * scaleFactor,
            });
        };
        // Appeler updateDimensions au montage pour initialiser les dimensions
        updateDimensions();

        // Ajouter un écouteur d'événement pour les changements de taille de la fenêtre
        window.addEventListener('resize', updateDimensions);

        // Nettoyer l'écouteur d'événement lors du démontage du composant
        return () => {
            window.removeEventListener('resize', updateDimensions);
        };
    }, []);

    useEffect(() => {
        if (step !== 'ar') return;

        const sceneEl = sceneRef.current;
        if (!sceneEl) return;

        let cancelled = false;
        let cleanupTargets: (() => void) | null = null;

        const onFound = (e: Event) => {
            const el = e.currentTarget as HTMLElement;
            const index = Number(el.dataset.targetIndex);
            if (Number.isNaN(index)) return;

            // 1) stoppe tout d'abord (évite “l’ancienne continue”)
            stopAllVideos();

            // 2) cache toutes les targets puis montre seulement celle trouvée
            const targets = sceneEl.querySelectorAll<HTMLElement>('[mindar-image-target]');
            targets.forEach((t) => t.setAttribute('visible', 'false'));
            el.setAttribute('visible', 'true');

            // 3) joue la bonne vidéo
            setCurrentTargetIndex(index);
            const video = document.querySelector(`#video-${index}`) as HTMLVideoElement | null;
            if (video) {
                video.currentTime = 0;
                video.play().catch(() => {});
            }
        };

        const onLost = (e: Event) => {
            const el = e.currentTarget as HTMLElement;
            const index = Number(el.dataset.targetIndex);
            if (Number.isNaN(index)) return;

            // stoppe la vidéo et cache la target
            el.setAttribute('visible', 'false');

            // seulement pause la vidéo, ne remet pas à 0 pour éviter les problèmes de chargement à chaque fois
            const video = document.querySelector(`#video-${index}`) as HTMLVideoElement | null;
            if (video) {
                video.pause();
                video.currentTime = 0;
            }
        };

        const attachTargetListeners = () => {
            const targets = sceneEl.querySelectorAll<HTMLElement>('[mindar-image-target]');
            targets.forEach((el) => {
                el.addEventListener('targetFound', onFound);
                el.addEventListener('targetLost', onLost);
            });

            cleanupTargets = () => {
                targets.forEach((el) => {
                    el.removeEventListener('targetFound', onFound);
                    el.removeEventListener('targetLost', onLost);
                });
            };
        };

        const start = async () => {
            // 1) attendre que le système MindAR soit prêt
            let tries = 0;
            while (!cancelled && tries < 200) {
                const mindar = sceneEl.systems?.['mindar-image-system'];
                if (mindar) {
                    try {
                        await mindar.start();
                    } catch (e) {
                        console.log('Camera permission error:', e);
                    }
                    break;
                }
                tries++;
                await new Promise((r) => setTimeout(r, 50));
            }

            if (cancelled) return;

            // 2) attendre que la scène A-Frame soit "loaded" pour être sûr que les targets existent
            if (sceneEl.hasLoaded) {
                attachTargetListeners();
            } else {
                const onLoaded = () => {
                    sceneEl.removeEventListener('loaded', onLoaded);
                    if (!cancelled) attachTargetListeners();
                };
                sceneEl.addEventListener('loaded', onLoaded);
            }
        };

        start();

        return () => {
            cancelled = true;

            // enlever listeners target
            cleanupTargets?.();

            stopAllVideos();
            hideAllTargets();

            // stopper mindar
            try {
                const mindar = sceneEl.systems?.['mindar-image-system'];
                mindar?.stop();
            } catch {
                // pas grave si ça plante, c’est que le système n’était pas prêt ou déjà arrêté
            }
        };
    }, [step]);

    const handleCheckAnswer = () => {
        // pause sans reset pour pouvoir reprendre
        stopAllVideos(false);
        hideAllTargets();

        const correct = currentTargetIndex === 2;
        setIsCorrectAnswer(correct);
        setStep('result');
    };

    const handleButtonClick = () => {
        if (step === 'intro') {
            setStep('ar');
            return;
        }

        if (step === 'ar') {
            handleCheckAnswer();
            return;
        }

        if (step === 'result') {
            setStep('ar');
            setTimeout(() => {
                showAllTargets();
                resumeCurrentVideo();
            }, 150);
        }
    };

    return (
        <div
            className='page-ar-container'
            style={{
                width: `${dimensions.width}px`,
                backgroundColor: step !== 'ar' ? '#2b5e83' : 'transparent',
            }}
        >
            {step !== 'result' && !isCorrectAnswer && (
                <MissionHeader
                    showBackButton={false}
                    showSettingsButton={false}
                    showCloseButton={true}
                    logoVariant='default'
                    closeButtonVariant='white'
                />
            )}
            <div
                className='page-ar-content'
                style={{
                    opacity: step === 'intro' ? 1 : 0,
                    transition: 'opacity 0.5s ease-in-out',
                }}
            >
                <h1 className='page-ar-title'>
                    Trouvez
                    <br />
                    le tueur
                </h1>
                <p className='page-ar-text'>
                    Les lunettes de notre allié devraient nous permettre de détecter le tueur enrobé
                    de sucre ! Scannez chacune des cartes des musiciens pour l'identifier...
                </p>
                <p className='page-ar-text'>
                    <b>
                        Attention !Pour cela, il vous faudra accepter l'utilisation de la caméra de
                        votre appareil.
                    </b>
                </p>
                <img
                    src='./images/phone-check.png'
                    alt='Phone check'
                    className='page-ar-icon'
                />
            </div>
            {step !== 'result' && !isCorrectAnswer && (
                <MainButton
                    text={step === 'ar' ? 'C’est lui le tueur !' : 'Commencer'}
                    backgroundColor='secondary'
                    onClick={() => {
                        handleButtonClick();
                    }}
                />
            )}

            {step === 'result' && !isCorrectAnswer && (
                <Debrief
                    isCorrectAnswer={isCorrectAnswer}
                    handleButtonClick={() => setStep('ar')}
                />
            )}

            {step === 'result' && isCorrectAnswer && (
                <div className='page-ar-result'>
                    <MissionHeader
                        showBackButton={false}
                        showSettingsButton={false}
                        showCloseButton={true}
                        logoVariant='default'
                        closeButtonVariant='tertiary'
                    />
                    <div className='page-ar-result-text'>
                        <h1>Bravo ! </h1>
                        <h2>Vous avez bien identifié le tristement célèbre sérotype 3. </h2>
                        <p>
                            Il présente une grande virulence car il est plus fortement encapsulé. 
                            Il est prédominant dans les otites moyennes aiguës causées par le pneumocoque. 
                            (Rapport annuel d’activité 2025 du CNRP. Année d’exercice 2024).
                        </p>
                        <img
                            src='./images/agent-image-5.png'
                            alt='Image agent'
                            className='questionPage-feedback-image'
                        />
                    </div>
                    <div className='page-ar-result-pioche'>
                        <p className='questionPage-feedback-pioche'>
                            Retournez la carte <span>32</span>
                        </p>
                        <p className='questionPage-feedback-pioche questionPage-feedback-pioche--white'>
                            Défaussez les cartes 19, 89, 21, 22 et 24
                        </p>
                    </div>
                </div>
            )}

            <div
                className='page-ar-canvas-container'
                style={{ opacity: step === 'ar' ? 1 : 0, transition: 'opacity 0.5s ease-in-out' }}
            >
                <a-scene
                    ref={sceneRef}
                    mindar-image={`imageTargetSrc: ./ar/targets.mind; maxTrack: 1; autoStart: false; uiScanning: false; uiLoading: no; uiError: no; filterMinCF: 0.001; filterBeta: 0.01;`}
                    color-space='sRGB'
                    renderer='colorManagement: true, physicallyCorrectLights'
                    vr-mode-ui='enabled: false'
                    embedded
                    device-orientation-permission-ui='enabled: false'
                >
                    <a-assets>
                        <img
                            id='target-0'
                            src='./ar/21-trigger.jpg'
                        />
                        <img
                            id='target-1'
                            src='./ar/22-trigger.jpg'
                        />
                        <img
                            id='target-2'
                            src='./ar/54-trigger.jpg'
                        />

                        <video
                            id='video-0'
                            src='./ar/video-0.mp4'
                            loop
                            muted
                            playsInline
                            preload='auto'
                            crossOrigin='anonymous'
                        />
                        <video
                            id='video-1'
                            src='./ar/video-1.mp4'
                            loop
                            muted
                            playsInline
                            preload='auto'
                            crossOrigin='anonymous'
                        />
                        <video
                            id='video-2'
                            src='./ar/video-2.mp4'
                            loop
                            muted
                            playsInline
                            preload='auto'
                            crossOrigin='anonymous'
                        />
                    </a-assets>

                    <a-camera
                        position='0 0 0'
                        look-controls='enabled: false'
                    />

                    <a-entity
                        key={0}
                        ref={target0}
                        id={`target-0`}
                        data-target-index='0'
                        mindar-image-target={`targetIndex: 0`}
                    >
                        <a-plane
                            src='#video-0'
                            // src={`./ar/video-0.mp4`}
                            position='0 0 0.001'
                            width='1'
                            height={1 * targetRatio}
                            material='shader: flat; transparent: true; repeat: 1 0.9902; offset: 0 0.0049;'
                        />
                    </a-entity>

                    <a-entity
                        key={1}
                        ref={target1}
                        id={`target-1`}
                        data-target-index='1'
                        mindar-image-target={`targetIndex: 1`}
                    >
                        <a-plane
                            src='#video-1'
                            position='0 0 0.001'
                            width='1'
                            height={1 * targetRatio}
                            material='shader: flat; transparent: true; repeat: 1 0.9902; offset: 0 0.0049;'
                        />
                    </a-entity>

                    <a-entity
                        key={2}
                        ref={target2}
                        id={`target-2`}
                        data-target-index='2'
                        mindar-image-target={`targetIndex: 2`}
                    >
                        <a-plane
                            src='#video-2'
                            position='0 0 0.001'
                            width='1'
                            height={1 * targetRatio}
                            material='shader: flat; transparent: true; repeat: 1 0.9902; offset: 0 0.0049;'
                        />
                    </a-entity>
                </a-scene>
            </div>
        </div>
    );
};

export default AR;
